import {
  Button,
  Checkbox,
  Field,
  Input,
  Text,
  tokens,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  Delete20Regular,
} from "@fluentui/react-icons";
import { useState } from "react";
import { Controller, type Control } from "react-hook-form";

import { ArrayOfObjectsTable } from "@/features/review/ArrayOfObjectsTable";
import {
  isArrayOfObjects,
  isArrayOfPrimitives,
  type FieldValues,
} from "@/features/review/fieldFormSchema";
import { formatWithThousands, parseNumeric } from "@/lib/formatNumber";

type FieldKind =
  | "string"
  | "number"
  | "boolean"
  | "array-of-objects"
  | "array-of-primitives"
  | "complex";

function isPrimitive(v: unknown): boolean {
  return (
    v === null ||
    typeof v === "string" ||
    typeof v === "number" ||
    typeof v === "boolean"
  );
}

function kindOf(value: unknown): FieldKind {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  if (value === null || typeof value === "string") return "string";
  if (isArrayOfObjects(value)) return "array-of-objects";
  if (isArrayOfPrimitives(value)) return "array-of-primitives";
  return "complex";
}

function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Numeric input that renders with thousands separators while idle and
 * falls back to the raw digit string on focus so the user can edit
 * without fighting the commas. The underlying form value stays as the
 * raw string — {@link fromFormValues} strips commas before coercion,
 * but we avoid ever storing them so number inputs round-trip cleanly.
 */
function NumericInput({
  value,
  onChange,
  disabled,
}: {
  value: unknown;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const raw =
    value === null || value === undefined ? "" : String(value).replace(/,/g, "");
  const display =
    focused || parseNumeric(raw) === null ? raw : formatWithThousands(raw);
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={display}
      onChange={(_, d) => onChange(d.value.replace(/,/g, ""))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      disabled={disabled}
    />
  );
}

export interface ExtractedFieldsFormProps {
  control: Control<FieldValues>;
  initial: Record<string, unknown>;
  readOnly: boolean;
}

/**
 * Renders an editable form for the extractedFields object.
 * Primitive scalars get typed inputs; complex shapes (arrays, nested
 * objects) fall back to a JSON textarea that submits as parsed JSON.
 */
export function ExtractedFieldsForm({
  control,
  initial,
  readOnly,
}: ExtractedFieldsFormProps) {
  const keys = Object.keys(initial);
  if (keys.length === 0) {
    return (
      <Text style={{ color: tokens.colorNeutralForeground2 }}>
        No extracted fields for this document.
      </Text>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      {keys.map((key) => {
        const value = initial[key];
        const kind = kindOf(value);
        const label = humanize(key);

        if (kind === "array-of-objects" && isArrayOfObjects(value)) {
          return (
            <div
              key={key}
              style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}
            >
              <Text
                size={200}
                weight="semibold"
                style={{ color: tokens.colorNeutralForeground2 }}
              >
                {label}
              </Text>
              <ArrayOfObjectsTable
                control={control}
                name={key}
                sample={value[0]!}
                readOnly={readOnly}
              />
            </div>
          );
        }

        // Editable list-of-primitives (e.g. nor_date_list) — one input
        // per item, add/remove buttons. Other complex shapes stay
        // read-only; opening them for edits would require a full
        // nested form schema we don't have yet.
        if (kind === "array-of-primitives" && isArrayOfPrimitives(value)) {
          return (
            <div
              key={key}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                minWidth: 0,
              }}
            >
              <Text
                size={200}
                weight="semibold"
                style={{ color: tokens.colorNeutralForeground2 }}
              >
                {label}
              </Text>
              <PrimitiveListField
                control={control}
                name={key}
                sample={value}
                readOnly={readOnly}
              />
            </div>
          );
        }
        if (kind === "complex") {
          return (
            <ReadOnlyField key={key} label={label}>
              <StructuredValue value={value} />
            </ReadOnlyField>
          );
        }

        return (
          <Controller
            key={key}
            name={key}
            control={control}
            render={({ field, fieldState }) => {
              const error = fieldState.error?.message;
              if (kind === "boolean") {
                return (
                  <Field label={label} validationMessage={error}>
                    <Checkbox
                      checked={Boolean(field.value)}
                      onChange={(_, d) => field.onChange(Boolean(d.checked))}
                      disabled={readOnly}
                    />
                  </Field>
                );
              }
              if (kind === "number") {
                return (
                  <Field label={label} validationMessage={error}>
                    <NumericInput
                      value={field.value}
                      onChange={field.onChange}
                      disabled={readOnly}
                    />
                  </Field>
                );
              }
              const stringValue =
                field.value === null || field.value === undefined
                  ? ""
                  : String(field.value);
              return (
                <Field label={label} validationMessage={error}>
                  <Input
                    type="text"
                    value={stringValue}
                    onChange={(_, d) => field.onChange(d.value)}
                    disabled={readOnly}
                  />
                </Field>
              );
            }}
          />
        );
      })}
    </div>
  );
}

type PrimitiveItem = string | number | boolean | null;

function detectPrimitiveKind(
  sample: ReadonlyArray<PrimitiveItem>,
): "string" | "number" | "boolean" {
  const item = sample.find((v) => v !== null);
  if (typeof item === "number") return "number";
  if (typeof item === "boolean") return "boolean";
  return "string";
}

/**
 * Editable list of scalar primitives — one row per item, with type-aware
 * inputs (numbers get thousands separators, booleans get a checkbox)
 * and add/remove buttons. The form value stays as a JS array so the
 * schema mapper can coerce each item back to its original type on save.
 */
function PrimitiveListField({
  control,
  name,
  sample,
  readOnly,
}: {
  control: Control<FieldValues>;
  name: string;
  sample: ReadonlyArray<PrimitiveItem>;
  readOnly: boolean;
}) {
  const itemKind = detectPrimitiveKind(sample);
  const emptyItem: PrimitiveItem = itemKind === "boolean" ? false : "";

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const arr: PrimitiveItem[] = Array.isArray(field.value)
          ? (field.value as PrimitiveItem[])
          : [];
        const updateAt = (idx: number, next: PrimitiveItem) => {
          const copy = [...arr];
          copy[idx] = next;
          field.onChange(copy);
        };
        const removeAt = (idx: number) => {
          field.onChange(arr.filter((_, i) => i !== idx));
        };
        const append = () => {
          field.onChange([...arr, emptyItem]);
        };

        return (
          <div
            style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}
          >
            {arr.length === 0 ? (
              <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                No items.
              </Text>
            ) : null}
            {arr.map((item, idx) => (
              <div
                key={idx}
                style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  {itemKind === "number" ? (
                    <NumericInput
                      value={item}
                      onChange={(next) => updateAt(idx, next)}
                      disabled={readOnly}
                    />
                  ) : itemKind === "boolean" ? (
                    <Checkbox
                      checked={Boolean(item)}
                      onChange={(_, d) => updateAt(idx, Boolean(d.checked))}
                      disabled={readOnly}
                    />
                  ) : (
                    <Input
                      type="text"
                      value={item === null || item === undefined ? "" : String(item)}
                      onChange={(_, d) => updateAt(idx, d.value)}
                      disabled={readOnly}
                    />
                  )}
                </div>
                {!readOnly ? (
                  <Button
                    appearance="subtle"
                    icon={<Delete20Regular />}
                    size="small"
                    aria-label="Delete item"
                    onClick={() => removeAt(idx)}
                  />
                ) : null}
              </div>
            ))}
            {!readOnly ? (
              <div>
                <Button
                  appearance="subtle"
                  icon={<Add20Regular />}
                  size="small"
                  onClick={append}
                >
                  Add item
                </Button>
              </div>
            ) : null}
          </div>
        );
      }}
    />
  );
}

const PRIMITIVE_PREVIEW_LIMIT = 8;
const COLLAPSED_LIST_LIMIT = 5;

function formatPrimitive(v: unknown): string {
  if (v === null) return "—";
  if (typeof v === "number") return formatWithThousands(String(v));
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

function ReadOnlyField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <Text
        size={200}
        weight="semibold"
        style={{ color: tokens.colorNeutralForeground2 }}
      >
        {label}
      </Text>
      {children}
    </div>
  );
}

function PrimitiveList({ items }: { items: ReadonlyArray<string | number | boolean | null> }) {
  const [expanded, setExpanded] = useState(false);
  const overflow = items.length > COLLAPSED_LIST_LIMIT;
  const visible = !expanded && overflow ? items.slice(0, COLLAPSED_LIST_LIMIT) : items;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <ul
        style={{
          margin: 0,
          paddingLeft: 20,
          color: tokens.colorNeutralForeground1,
        }}
      >
        {visible.map((item, idx) => (
          <li key={idx}>
            <Text size={200}>{formatPrimitive(item)}</Text>
          </li>
        ))}
      </ul>
      {overflow ? (
        <Button
          appearance="subtle"
          size="small"
          onClick={() => setExpanded((v) => !v)}
          icon={
            expanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />
          }
        >
          {expanded ? "Show less" : `Show ${items.length - COLLAPSED_LIST_LIMIT} more`}
        </Button>
      ) : null}
    </div>
  );
}

function ObjectTable({ rows }: { rows: ReadonlyArray<Record<string, unknown>> }) {
  const columns = Array.from(
    new Set(rows.flatMap((r) => Object.keys(r))),
  );
  return (
    <div
      style={{
        overflowX: "auto",
        maxWidth: "100%",
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: 4,
      }}
    >
      <table
        style={{
          borderCollapse: "collapse",
          fontSize: 13,
          minWidth: "max-content",
        }}
      >
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                style={{
                  textAlign: "left",
                  padding: "6px 8px",
                  borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                  color: tokens.colorNeutralForeground2,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {humanize(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {columns.map((col) => (
                <td
                  key={col}
                  style={{
                    padding: "4px 8px",
                    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
                    verticalAlign: "top",
                  }}
                >
                  <StructuredValue value={row[col]} compact />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StructuredValue({
  value,
  compact = false,
}: {
  value: unknown;
  compact?: boolean;
}) {
  if (isPrimitive(value)) {
    return <Text size={compact ? 200 : 300}>{formatPrimitive(value)}</Text>;
  }
  if (isArrayOfObjects(value)) {
    return <ObjectTable rows={value} />;
  }
  if (isArrayOfPrimitives(value)) {
    if (compact) {
      const preview = value.slice(0, PRIMITIVE_PREVIEW_LIMIT).map(formatPrimitive).join(", ");
      const suffix = value.length > PRIMITIVE_PREVIEW_LIMIT ? ` (+${value.length - PRIMITIVE_PREVIEW_LIMIT} more)` : "";
      return <Text size={200}>{preview}{suffix}</Text>;
    }
    return <PrimitiveList items={value} />;
  }
  if (Array.isArray(value)) {
    // Heterogeneous array — render each item recursively in a list.
    return (
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {value.map((item, idx) => (
          <li key={idx}>
            <StructuredValue value={item} compact={compact} />
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === "object" && value !== null) {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return (
        <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
          —
        </Text>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {entries.map(([k, v]) => (
          <div
            key={k}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(120px, max-content) 1fr",
              gap: 8,
              alignItems: "baseline",
            }}
          >
            <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
              {humanize(k)}
            </Text>
            <StructuredValue value={v} compact />
          </div>
        ))}
      </div>
    );
  }
  return (
    <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
      {String(value)}
    </Text>
  );
}
